/**
 * Records which platform APIs the pipeline had to degrade away from.
 *
 * The guards live in `browserApis`/`storage`/`correlationId`, which are
 * reporter-unaware by design — they are called from `buildEvent` and from
 * inside the queue lock, neither of which has access to the plugin's
 * `reportMessage`. They note here instead, and the plugin drains it.
 */
export type DegradationKind =
  | "crypto-random-uuid"
  | "session-storage"
  | "local-storage"
  | "web-locks"
  /**
   * An event held a value `JSON.stringify` refuses — a circular structure, or a
   * BigInt. Its own kind rather than folded into `local-storage`: storage is
   * fine, one event is not, and reporting it as a storage problem sends whoever
   * investigates looking for a quota issue that does not exist.
   */
  | "unserializable-event";

/**
 * Kinds noted since the last drain.
 *
 * A set rather than a counter: these guards sit on the hot path —
 * `getCorrelationId` and `writeQueue` run for every event — so reporting per
 * occurrence would mean one Sentry entry per click for the whole session. The
 * useful signal is that a browser degraded at all, not how many times.
 */
const pending = new Set<DegradationKind>();

/**
 * Kinds already handed to a drain. Most guards re-note on every call —
 * `withQueueLock` and `getRandomUuid` have no latch to hide behind — so without
 * this a permanent degradation would be re-reported on every flush for the life
 * of the page. Once per kind is the whole signal: the browser degraded.
 */
const alreadyDrained = new Set<DegradationKind>();

/** Called from the guard's catch. Safe to call on every event. */
export function noteDegradation(kind: DegradationKind): void {
  if (alreadyDrained.has(kind)) {
    return;
  }
  pending.add(kind);
}

/**
 * Returns the kinds noted since the last call and forgets them.
 *
 * Draining rather than peeking is what makes reporting once safe: the plugin
 * calls this on every flush, and a kind that stayed resident would be
 * re-reported on each one. A degradation that begins later still surfaces on
 * the next drain — localStorage typically fills mid-session, long after the
 * first flush.
 */
export function drainDegradations(): DegradationKind[] {
  const drained = [...pending];
  pending.clear();
  for (const kind of drained) {
    alreadyDrained.add(kind);
  }
  return drained;
}

/** Test seam: forgets everything noted so far. Not exported from the package. */
export function resetDegradations(): void {
  pending.clear();
  alreadyDrained.clear();
}
